import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { eventBookingService } from '../services/eventBooking.service';
import { EventBookingRequest, EventBookingResponse } from '../types/eventBooking';
import { useUserProfile } from './useUserProfile';
import { Event } from '../types/event';

const BOOKED_EVENTS_KEY = 'maroline_booked_events';

export const useEventBooking = () => {
  const queryClient = useQueryClient();
  const { data: userData } = useUserProfile();
  const [localBookedEvents, setLocalBookedEvents] = useState<string[]>([]);

  // Load booked events from localStorage on mount
  useEffect(() => {
    const storedBookedEvents = localStorage.getItem(BOOKED_EVENTS_KEY);
    if (storedBookedEvents) {
      setLocalBookedEvents(JSON.parse(storedBookedEvents));
    }
  }, []);

  // Use campaignMembers if available, otherwise fall back to localStorage
  const bookedEvents = userData?.campaignMembers || localBookedEvents;

  const mutation = useMutation<EventBookingResponse, Error, EventBookingRequest>({
    mutationFn: (bookingData) => eventBookingService.bookEvent(bookingData),
    onSuccess: (_, variables) => {
      // Get all active event queries
      const eventQueries = queryClient.getQueriesData<Event[]>({ queryKey: ['events'] });

      // Update all event query caches (dashboard and city-filtered)
      eventQueries.forEach(([queryKey, oldData]) => {
        queryClient.setQueryData(queryKey, (old: Event[] | undefined = oldData) => {
          if (!old || !Array.isArray(old)) return [];
          return old.map(event => {
            const eventId = (event as any)?.appointmentId?.toString();
            if (eventId === variables.eventId) {
              return { ...event, isBooked: true };
            }
            return event;
          });
        });
      });

      // Optionally log which caches were updated
      console.debug('Updated event caches:', eventQueries.map(([key]) => key));

      // Update localStorage as fallback
      const updatedBookedEvents = [...localBookedEvents, variables.eventId];
      localStorage.setItem(BOOKED_EVENTS_KEY, JSON.stringify(updatedBookedEvents));
      setLocalBookedEvents(updatedBookedEvents);
    }
  });

  const isEventBooked = (eventId: string): boolean => {
    if (userData?.campaignMembers) {
      return userData.campaignMembers.some(member => 
        member.campaignId.toString() === eventId && 
        member.memberId === userData.id
      );
    }
    return localBookedEvents.includes(eventId);
  };

  return {
    bookEvent: mutation.mutate,
    bookEventAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    isEventBooked,
    bookedEvents
  };
};
